import { SesReadiness, SesConfirmDialog377, SesConfirmDialog397, SesConnectionStatus, SesLiveGates, SesControlsSection, SesSuppressionSection, SesFeedbackSection, muted, fmt } from "./EmailSesModulesSections";
import { stableRecordKey } from '../../lib/rowIdentity';
import React, { useState, useEffect, useCallback } from 'react';
import { Cloud, Send, RefreshCw, Pause, Play, ShieldAlert, Loader2, Bell } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { api } from '../../App';
// Amazon SES delivery card (Settings tab). Credentials live only in server environment variables and are
// never shown here; this panel only reads status, adjusts safety controls and sends a single test email.
export function SesDeliveryCard({
  token,
  role
}) {
  const [st, setSt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [controls, setControls] = useState({});
  const [suppressEmail, setSuppressEmail] = useState('');
  const [switchDialog, setSwitchDialog] = useState(null); // 'ses' | 'smtp'
  const [gateDialog, setGateDialog] = useState(null); // { key, label, on }
  const [gateTick, setGateTick] = useState(false);
  const [cfgSet, setCfgSet] = useState('');
  const [topicArn, setTopicArn] = useState('');
  const [suppList, setSuppList] = useState(null);
  const [suppQuery, setSuppQuery] = useState('');
  const [epCheck, setEpCheck] = useState(null);
  const checkEndpoint = async () => {
    setBusy(true);
    try {
      setEpCheck(await api.getAuth('/api/admin/email/ses/sns/endpoint-check', token));
    } catch (e) {
      toast.error(e.message);
    }
    setBusy(false);
  };
  const [tick, setTick] = useState(false);
  const isAdmin = role === 'admin';
  const load = useCallback(async (force = false) => {
    try {
      const d = await api.getAuth(`/api/admin/email/ses/status${force ? '?force=1' : ''}`, token);
      setSt(d);
      setControls({
        ses_max_rate_per_sec: d.controls?.ses_max_rate_per_sec ?? 5,
        ses_daily_safety_limit: d.controls?.ses_daily_safety_limit ?? 5000,
        ses_max_retries: d.controls?.ses_max_retries ?? 3,
        ses_retry_base_seconds: d.controls?.ses_retry_base_seconds ?? 120,
        ses_aws_rate_limit: d.controls?.ses_aws_rate_limit ?? 14,
        ses_aws_daily_limit: d.controls?.ses_aws_daily_limit ?? 50000
      });
      setCfgSet(d.controls?.ses_configuration_set || '');
      setTopicArn(d.controls?.ses_sns_topic_arn || '');
    } catch (e) {/* non-critical for other roles */}
    setLoading(false);
  }, [token]);
  useEffect(() => {
    load();
  }, [load]);
  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(okMsg || r?.message || 'Done');
      await load();
    } catch (e) {
      toast.error(e.message);
    }
    setBusy(false);
  };
  const saveControls = () => run(() => api.putAuth('/api/admin/email/ses/settings', {
    ses_max_rate_per_sec: parseInt(controls.ses_max_rate_per_sec, 10),
    ses_daily_safety_limit: parseInt(controls.ses_daily_safety_limit, 10),
    ses_max_retries: parseInt(controls.ses_max_retries, 10),
    ses_retry_base_seconds: parseInt(controls.ses_retry_base_seconds, 10),
    ses_aws_rate_limit: parseInt(controls.ses_aws_rate_limit, 10),
    ses_aws_daily_limit: parseInt(controls.ses_aws_daily_limit, 10)
  }, token), 'Safety controls saved');
  const sendTest = () => {
    if (!testTo.trim() || !testTo.includes('@')) {
      toast.error('Please enter a valid email address for the test');
      return;
    }
    run(() => api.postAuth('/api/admin/email/ses/test', {
      to: testTo.trim()
    }, token)).then(() => setTestTo(''));
  };
  const switchProvider = () => {
    const target = switchDialog;
    setSwitchDialog(null);
    setTick(false);
    run(() => api.putAuth('/api/admin/email/ses/settings', {
      email_provider: target,
      confirm_provider_change: true
    }, token), target === 'ses' ? 'Amazon SES is now the live email provider' : 'Own mailbox (SMTP) is now the live email provider');
  };
  const loadSuppressions = useCallback(async q => {
    try {
      setSuppList(await api.getAuth(`/api/admin/email/suppressions${q ? `?q=${encodeURIComponent(q)}` : ''}`, token));
    } catch {
      setSuppList({
        items: [],
        total: 0
      });
    }
  }, [token]);
  useEffect(() => {
    if (role === 'admin') loadSuppressions('');
  }, [role, loadSuppressions]);
  const saveSnsConfig = () => run(() => api.putAuth('/api/admin/email/ses/settings', {
    ses_configuration_set: cfgSet.trim(),
    ses_sns_topic_arn: topicArn.trim()
  }, token), 'Feedback settings saved');
  const GATES = [['transactional', 'Transactional email', 'tickets, membership, verification codes, renewals, digital cards'], ['newsletters', 'Newsletters & event blasts', 'marketing messages to subscribers'], ['migration', 'Membership migration', 'individual claim invitations and reminders']];
  const toggleGate = () => {
    const g = gateDialog;
    setGateDialog(null);
    setGateTick(false);
    run(() => api.putAuth('/api/admin/email/ses/settings', {
      [`ses_live_${g.key}`]: g.on,
      confirm_activation: true
    }, token), `${g.label} live sending switched ${g.on ? 'ON' : 'OFF'}`);
  };
  const copyEndpoint = async () => {
    try {
      await navigator.clipboard.writeText(st?.sns?.endpoint || '');
      toast.success('Endpoint copied');
    } catch {
      toast.error('Could not copy — please select the text manually');
    }
  };
  if (loading) return <Card data-testid="ses-card-loading"><CardContent className="p-6 space-y-3"><Skeleton className="h-5 w-64" /><Skeleton className="h-16 w-full" /></CardContent></Card>;
  if (!st) return null;
  const acct = st.account || {};
  const q = st.queue || {};
  const isSes = st.provider === 'ses';
  const connOk = acct.connection === 'ok';
  const configured = acct.configured;
  return <Card data-testid="ses-delivery-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 flex-wrap">
          <Cloud className="w-5 h-5" />
          Amazon SES Delivery
          <Badge variant={isSes ? 'default' : 'outline'} data-testid="ses-provider-badge">Live provider: {st.provider_label}</Badge>
          {q.paused && <Badge variant="outline" className="border-amber-500/70 text-amber-700" data-testid="ses-queue-paused-badge">Queue paused</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* connection */}
        <SesConnectionStatus acct={acct} busy={busy} configured={configured} connOk={connOk} load={load} />

        {/* readiness checklist (mirrors docs/SES_SETUP.md section 8) */}
        <SesReadiness acct={acct} configured={configured} connOk={connOk} isSes={isSes} st={st} />

        {/* alerts */}
        {(st.alerts || []).length > 0 && <div className="space-y-2" data-testid="ses-alerts">
            {st.alerts.map(a => <div key={a.alert_id} className="flex items-start gap-2 p-2.5 rounded-md border border-amber-500/40 bg-amber-500/10 text-sm">
                <Bell className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                <span className="flex-1">{a.message}</span>
                {isAdmin && <Button size="sm" variant="ghost" onClick={() => run(() => api.postAuth(`/api/admin/email/alerts/${a.alert_id}/resolve`, {}, token), 'Dismissed')} data-testid={`ses-alert-dismiss-${a.kind}`}>Dismiss</Button>}
              </div>)}
          </div>}

        {/* queue + usage */}
        <div className="flex flex-wrap gap-2" data-testid="ses-queue-badges">
          <Badge variant="outline">Queued: {q.queued ?? 0}</Badge>
          <Badge variant="outline">Retrying: {q.temporarily_failed ?? 0}</Badge>
          <Badge variant="outline" className={q.requires_review ? 'border-red-500/60 text-red-700' : ''}>Needs review: {q.requires_review ?? 0}</Badge>
          <Badge variant="outline" className="border-green-500/60 text-green-700">Sent today: {st.usage?.sent_today ?? 0} / {st.usage?.daily_safety_limit}</Badge>
          <Badge variant="outline">Delivered today: {q.delivered_today ?? 0}</Badge>
          <Badge variant="outline">Hard bounces: {q.hard_bounced ?? 0}</Badge>
          <Badge variant="outline">Complaints: {q.complained ?? 0}</Badge>
          <Badge variant="outline">Suppressed addresses: {q.suppressed_addresses ?? 0}</Badge>
        </div>
        <p className={`text-xs ${muted}`} data-testid="ses-last-activity">
          Last successful send: {fmt(st.last_success_at)} · Last failure: {st.last_failure ? `${st.last_failure} at ${fmt(st.last_failure_at)}` : 'none'}
          {q.pause_reason && <> · Pause reason: {q.pause_reason}</>}
        </p>

        {isAdmin && <>
            <Separator />
            {/* safety controls */}
            <SesControlsSection busy={busy} controls={controls} saveControls={saveControls} setControls={setControls} st={st} />

            {/* test + queue actions */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Input type="email" value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="Send an SES test email to..." className="sm:w-72" data-testid="ses-test-input" />
              <Button onClick={sendTest} disabled={busy || !configured} variant="outline" data-testid="ses-test-send-button" title={configured ? 'Sends one test email through Amazon SES' : 'Add the SES environment variables first'}>
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send SES test
              </Button>
              {q.paused ? <Button onClick={() => run(() => api.postAuth('/api/admin/email/ses/queue/resume', {}, token))} disabled={busy} variant="outline" data-testid="ses-queue-resume-button"><Play className="w-4 h-4 mr-2" /> Resume queue</Button> : <Button onClick={() => run(() => api.postAuth('/api/admin/email/ses/queue/pause', {}, token))} disabled={busy} variant="outline" data-testid="ses-queue-pause-button"><Pause className="w-4 h-4 mr-2" /> Pause queue</Button>}
              {(q.requires_review ?? 0) > 0 && <Button onClick={() => run(() => api.postAuth('/api/admin/email/ses/queue/retry-review', {}, token))} disabled={busy} variant="outline" data-testid="ses-retry-review-button"><RefreshCw className="w-4 h-4 mr-2" /> Retry reviewed</Button>}
            </div>

            {/* live activation gates */}
            <SesLiveGates GATES={GATES} busy={busy} isSes={isSes} q={q} setGateDialog={setGateDialog} st={st} />

            {/* provider switch */}
            <div className="p-3 rounded-lg border border-[hsl(var(--border))] space-y-2" data-testid="ses-provider-section">
              <Label className="font-semibold flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> Live email provider</Label>
              <p className={`text-xs ${muted}`}>
                Exactly one provider sends email; there is never an automatic fallback. Switching to Amazon SES requires the connection check and a successful SES test email first.
                {st.activated_at && <> SES activated {fmt(st.activated_at)}.</>}
              </p>
              {isSes ? <Button variant="outline" size="sm" onClick={() => setSwitchDialog('smtp')} disabled={busy} data-testid="ses-switch-smtp-button">Switch back to own mailbox (SMTP)</Button> : <Button size="sm" onClick={() => setSwitchDialog('ses')} disabled={busy || !connOk} data-testid="ses-switch-ses-button">Make Amazon SES the live provider</Button>}
            </div>

            {/* SNS feedback */}
            <SesFeedbackSection busy={busy} cfgSet={cfgSet} checkEndpoint={checkEndpoint} copyEndpoint={copyEndpoint} epCheck={epCheck} run={run} saveSnsConfig={saveSnsConfig} setCfgSet={setCfgSet} setTopicArn={setTopicArn} st={st} token={token} topicArn={topicArn} />

            {/* suppression */}
            <SesSuppressionSection busy={busy} loadSuppressions={loadSuppressions} run={run} setSuppQuery={setSuppQuery} setSuppressEmail={setSuppressEmail} suppList={suppList} suppQuery={suppQuery} suppressEmail={suppressEmail} token={token} />

            {/* recent failures */}
            {(st.recent_failures || []).length > 0 && <div className="text-xs space-y-1" data-testid="ses-recent-failures">
                <Label className="font-semibold">Recent problems</Label>
                {st.recent_failures.slice(0, 6).map((f, i) => <div key={stableRecordKey(f)} className={`flex flex-wrap gap-2 ${muted}`}><span>{fmt(f.when)}</span><Badge variant="outline">{f.status}</Badge><span>{f.code}</span><span>{f.template}</span><span>{f.to}</span></div>)}
              </div>}
          </>}
      </CardContent>

      <SesConfirmDialog377 busy={busy} setSwitchDialog={setSwitchDialog} setTick={setTick} switchDialog={switchDialog} switchProvider={switchProvider} tick={tick} />
      <SesConfirmDialog397 busy={busy} gateDialog={gateDialog} gateTick={gateTick} setGateDialog={setGateDialog} setGateTick={setGateTick} toggleGate={toggleGate} />
    </Card>;
}
